<template>
  <div
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <div class="p-6">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-semibold text-gray-800">Détails de l'utilisateur</h3>
            <p class="text-sm text-gray-500">ID: {{ user.id }}</p>
          </div>
          <button
            @click="$emit('close')"
            class="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <icon name="X" :size="20" class="text-gray-500" />
          </button>
        </div>

        <div class="flex items-center gap-4 mb-6">
          <div class="w-20 h-20 rounded-full flex items-center justify-center"
               :class="user.role == 1 ? 'bg-purple-100' : 'bg-blue-100'">
            <span class="text-3xl font-bold" :class="user.role == 1 ? 'text-purple-600' : 'text-blue-600'">
              {{ getUserInitials(user) }}
            </span>
          </div>
          <div>
            <h4 class="font-semibold text-xl text-gray-800">{{ user.name }}</h4>
            <div class="flex items-center gap-2 mt-1">
              <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`">
                {{ getRoleLabel(user.role) }}
              </span>
              <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`">
                {{ getStatusLabel(user.status) }}
              </span>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <!-- Informations personnelles -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="User" :size="16" /> Informations personnelles
            </h4>
            <div class="p-4 bg-gray-50 rounded-lg space-y-3 text-sm">
              <div class="flex items-center gap-3 text-gray-700">
                <icon name="Mail" :size="14" class="text-gray-400 flex-shrink-0" />
                <div>
                  <p class="font-medium text-gray-900">Email</p>
                  <p class="text-gray-600">{{ user.email }}</p>
                </div>
              </div>
              <div class="flex items-center gap-3 text-gray-700">
                <icon name="Phone" :size="14" class="text-gray-400 flex-shrink-0" />
                <div>
                  <p class="font-medium text-gray-900">Téléphone</p>
                  <p class="text-gray-600">{{ user.phone || 'Non renseigné' }}</p>
                </div>
              </div>
              <div v-if="user.address" class="flex items-start gap-3 text-gray-700">
                <icon name="MapPin" :size="14" class="text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p class="font-medium text-gray-900">Adresse</p>
                  <p class="text-gray-600 whitespace-pre-line">{{ user.address }}</p>
                </div>
              </div>
              <div class="flex items-center gap-3 text-gray-700">
                <icon name="Calendar" :size="14" class="text-gray-400 flex-shrink-0" />
                <div>
                  <p class="font-medium text-gray-900">Inscription</p>
                  <p class="text-gray-600">{{ formatDateFull(user.created_at) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Statistiques -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="BarChart2" :size="16" /> Statistiques
            </h4>
            <div class="p-4 bg-gray-50 rounded-lg space-y-3 text-sm">
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Commandes totales</span>
                <span class="font-medium text-gray-900">{{ user.orders_count || user.orders?.length || 0 }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-gray-600">Total dépensé</span>
                <span class="font-medium text-gray-900">{{ formatPrice(user.total_spent || 0) }}</span>
              </div>
              <div v-if="user.last_order_date" class="flex justify-between items-center">
                <span class="text-gray-600">Dernière commande</span>
                <span class="font-medium text-gray-900">{{ formatDate(user.last_order_date) }}</span>
              </div>
              <div v-if="user.avg_order_value" class="flex justify-between items-center">
                <span class="text-gray-600">Panier moyen</span>
                <span class="font-medium text-gray-900">{{ formatPrice(user.avg_order_value) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Dernières commandes -->
        <div v-if="user.orders && user.orders.length > 0" class="mb-6">
          <h4 class="font-medium text-gray-700 mb-4 flex items-center gap-2">
            <icon name="Package" :size="16" /> Dernières commandes
          </h4>
          <div class="space-y-2">
            <div v-for="order in recentOrders" :key="order.id" class="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
              <div>
                <p class="font-medium text-sm">Commande #{{ order.order_number }}</p>
                <p class="text-xs text-gray-500">{{ formatDate(order.created_at) }}</p>
              </div>
              <div class="text-right">
                <span :class="`px-2 py-0.5 rounded text-xs font-medium ${getOrderStatusColor(order.status)}`">
                  {{ getOrderStatusLabel(order.status) }}
                </span>
                <p class="text-sm font-medium mt-1">{{ formatPrice(order.total) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            @click="$emit('close')"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center justify-center"
          >
            <icon name="X" :size="16" class="mr-2" />
            Fermer
          </button>
          
          <div class="flex-1 grid grid-cols-2 gap-3">
            <select
              :value="user.role"
              @change="$emit('update-role', { userId: user.id, newRole: $event.target.value })"
              :disabled="updatingRole"
              class="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="0">Client</option>
              <option value="1">Administrateur</option>
            </select>
            
            <select
              :value="user.status"
              @change="$emit('update-status', { userId: user.id, newStatus: $event.target.value })"
              :disabled="updatingStatus"
              class="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="1">Actif</option>
              <option value="0">Inactif</option>
              <option value="2">Bloqué</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserDetailModal',
  
  props: {
    user: {
      type: Object,
      required: true
    },
    updatingRole: {
      type: Boolean,
      default: false
    },
    updatingStatus: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    recentOrders() {
      return (this.user.orders || []).slice(0, 3).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  
  methods: {
    getUserInitials(user) {
      if (!user.name) return '?'
      return user.name
        .split(' ')
        .map(part => part.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },
    
    getRoleColor(role) {
      switch (parseInt(role)) {
        case 1: return 'bg-purple-100 text-purple-800 border border-purple-200'
        case 0: return 'bg-blue-100 text-blue-800 border border-blue-200'
        default: return 'bg-gray-100 text-gray-800 border border-gray-200'
      }
    },
    
    getRoleLabel(role) {
      const roleNum = parseInt(role)
      const labels = {
        1: 'Administrateur',
        0: 'Client'
      }
      return labels[roleNum] || `Rôle ${role}`
    },
    
    getStatusColor(status) {
      switch (parseInt(status)) {
        case 1: return 'bg-green-100 text-green-800 border border-green-200'
        case 0: return 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        case 2: return 'bg-red-100 text-red-800 border border-red-200'
        default: return 'bg-gray-100 text-gray-800 border border-gray-200'
      }
    },
    
    getStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        1: 'Actif',
        0: 'Inactif',
        2: 'Bloqué'
      }
      return labels[statusNum] || `Statut ${status}`
    },
    
    getOrderStatusColor(status) {
      switch (parseInt(status)) {
        case 4: return 'bg-green-100 text-green-800'
        case 3: return 'bg-blue-100 text-blue-800'
        case 2: return 'bg-purple-100 text-purple-800'
        case 1: return 'bg-yellow-100 text-yellow-800'
        case 0: return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getOrderStatusLabel(status) {
      const statusNum = parseInt(status)
      const labels = {
        0: 'Rejeté',
        1: 'En cours',
        2: 'Accepté',
        3: 'Expédié',
        4: 'Livré'
      }
      return labels[statusNum] || `Statut ${status}`
    },
    
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return dateString
        return date.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },
    
    formatDateFull(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return dateString
        return date.toLocaleDateString('fr-FR', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    }
  }
}
</script>